'use client';

import {cva, type VariantProps} from 'class-variance-authority';
import {Children, isValidElement} from 'react';

import {cn} from '../../lib/utils';
import {Dot} from '../Dot';

import type * as React from 'react';

const badgeVariantsConfig = {
  variants: {
    variant: {
      neutral: 'bg-background-neutral border-border-neutral text-text-neutral',
      success: 'bg-background-success border-border-success text-text-success',
      warning: 'bg-background-warning border-border-warning text-text-warning',
      error: 'bg-background-error border-border-error text-text-error',
      info: 'bg-background-info border-border-info text-text-info',
      progress: 'bg-background-progress border-border-progress text-text-progress',
      purple: 'bg-background-purple border-border-purple text-text-purple',
      brand: 'bg-brand-primary border-brand-primary text-fg_on-brand',
      'brand-subtle': 'border-brand-primary/[16%] bg-brand-primary/[6%] text-brand-primary',
      count: 'border-border bg-background-secondary text-fg-tertiary',
      outline: 'border-border bg-background-surface_hover text-fg-secondary',
      surface: 'border-border bg-background-surface text-fg'
    },
    size: {
      lg: 'text-sm leading-5 px-2.5 py-1',
      md: 'text-xs leading-[18px] px-2.5 py-0.5',
      sm: 'text-xs leading-[18px] px-2 py-0.5',
      xs: 'text-[10px] leading-[14px] px-1.5 py-0'
    }
  },
  defaultVariants: {
    variant: 'neutral',
    size: 'lg'
  }
} as const;

const badgeVariants = cva(
  'inline-flex max-w-full overflow-hidden items-center gap-1.5 rounded-full border font-medium whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2 [&>svg]:shrink-0',
  badgeVariantsConfig
);

function Badge({
  className,
  variant,
  size,
  dot,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & {dot?: boolean}) {
  return (
    <span data-slot="badge" className={cn(badgeVariants({variant, size}), className)} {...props}>
      {dot && (
        <Dot colorClassName={variant === 'success' ? 'bg-indication-success' : 'bg-current'} />
      )}
      {Children.map(props.children, child =>
        isValidElement(child) ? (
          child
        ) : (
          <span data-slot="badge-label" className="truncate">
            {child}
          </span>
        )
      )}
    </span>
  );
}

export {Badge, badgeVariants, badgeVariantsConfig};
